/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

import React from 'react'

import { EuiLoadingSpinner } from '@elastic/eui'

import type { User } from '@modules/cloud-api/v1/types'
import type { AsyncRequestState } from '@modules/ui-types'
import type { DeepPartial } from '@modules/ts-essentials'

import NativeUserProfileForm from './NativeUserProfileForm'
import ExternalUserProfileForm from './ExternalUserProfileForm'
import SystemUserProfileForm from './SystemUserProfileForm'

import type { FunctionComponent } from 'react'

export interface Props {
  currentUser: User | null
  onUpdateProfile: (user: DeepPartial<User>) => Promise<unknown>
  onChangePassword: (user: DeepPartial<User>) => Promise<unknown>
  onUpdateProfileRequest: AsyncRequestState
  onChangePasswordRequest: AsyncRequestState
}

const UpdateProfile: FunctionComponent<Props> = ({ currentUser, ...rest }) => {
  if (currentUser == null) {
    return <EuiLoadingSpinner />
  }

  if (currentUser.builtin) {
    return <SystemUserProfileForm currentUser={currentUser} {...rest} />
  }

  if (currentUser.security.security_realm?.type !== 'native') {
    return <ExternalUserProfileForm currentUser={currentUser} {...rest} />
  }

  return <NativeUserProfileForm currentUser={currentUser} {...rest} />
}

export default UpdateProfile
